<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SalesRegistrationPermissionState from '../components/SalesRegistrationPermissionState.vue';
import SalesPropertyRegistrationFooter from '../components/SalesPropertyRegistrationFooter.vue';
import SalesPropertyRegistrationHeader from '../components/SalesPropertyRegistrationHeader.vue';
import SalesPropertyRegistrationWorkspace from '../components/SalesPropertyRegistrationWorkspace.vue';
import SalesPropertyReviewCorrectionsDialog from '../components/SalesPropertyReviewCorrectionsDialog.vue';
import { PROPERTY_SECTION_KEYS } from '../composables/salesPropertyFormOptions';
import { useSalesPropertyForm } from '../composables/useSalesPropertyForm';
import { useSalesPropertyCatalog } from '../composables/useSalesPropertyCatalog';
import { useSalesPropertyCertificateChecks } from '../composables/useSalesPropertyCertificateChecks';
import { useSalesPropertyRegistrationPage } from '../composables/useSalesPropertyRegistrationPage';
import { useSalesReviewCheckCompletion } from '../composables/useSalesReviewCheckCompletion';
import { useSalesRegistration } from '../composables/useSalesRegistration';
import { salesPropertyReviewContext } from '../composables/salesPropertyReviewContext';
import { shouldReturnToSalesReview } from '../composables/salesReviewNavigation';

const DETAIL_PROPERTY_SECTION_KEYS = [
  'address',
  'registry',
  'laudemio',
  'values',
  'deadline',
  'clauses',
];

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.salesTransactionId);
const registration = useSalesRegistration({ id, t, notify: useAlert });
const propertyCatalog = useSalesPropertyCatalog({ id });
const certificateChecks = useSalesPropertyCertificateChecks({ id });
const reviewMode = computed(() => shouldReturnToSalesReview(route));
const propertyDetailMode = computed(() => route.query?.return_to === 'detail');
const referenceLayout = computed(
  () => !reviewMode.value && !propertyDetailMode.value
);
const propertyProgress = computed(
  () => Number(registration.transaction.value?.progress?.percent) || 0
);
const detailSection = computed(() =>
  DETAIL_PROPERTY_SECTION_KEYS.includes(route.query?.section)
    ? route.query.section
    : 'address'
);
const reviewContext = computed(() =>
  salesPropertyReviewContext(registration.transaction.value)
);
const reviewCompletion = useSalesReviewCheckCompletion({
  id,
  transaction: registration.transaction,
  t,
  notify: useAlert,
});
const reviewSections = computed(() => {
  if (reviewMode.value && reviewContext.value.sectionKeys.length) {
    return reviewContext.value.sectionKeys;
  }
  if (propertyDetailMode.value) return [detailSection.value];

  return PROPERTY_SECTION_KEYS;
});
const form = useSalesPropertyForm({
  id,
  transaction: registration.transaction,
  t,
  notify: useAlert,
  initialSection: detailSection,
  propertyDetailMode,
  sectionKeys: reviewSections,
});
const page = useSalesPropertyRegistrationPage({
  form,
  id,
  certificateChecks,
  propertyCatalog,
  registration,
  reviewCompletion,
  reviewContext,
  reviewMode,
  route,
  router,
  t,
});
</script>

<template>
  <div
    class="min-h-full bg-lais-bg-soft"
    :class="referenceLayout ? 'relative min-h-[calc(100vh-66px)]' : ''"
  >
    <SalesRegistrationPermissionState
      v-if="registration.isForbidden.value"
      @back="router.back()"
    />
    <template v-else>
      <SalesPropertyRegistrationHeader
        v-if="!referenceLayout"
        :current-title="page.currentTitle.value"
        :saving="form.saving.value"
        @back="page.goBack"
        @save="page.save"
      />
      <SalesPropertyRegistrationWorkspace
        :reference-layout="referenceLayout"
        :transaction="registration.transaction.value"
        :loading="registration.loading.value"
        :progress-label="page.progressLabel.value"
        :section-component="page.currentSection.value"
        :section-bindings="page.currentSectionBindings.value"
        :section-listeners="page.currentSectionListeners.value"
        :active-section="form.activeSection.value"
        :section-checks="reviewContext.sectionChecks"
        :section-keys="reviewSections"
        :current-index="form.currentIndex.value"
        :profile="form.profile.value"
        :errors="form.errors.value"
        :property="form.property.value"
        :review-count="reviewContext.sectionKeys.length"
        :review-mode="reviewMode"
        :saving="form.saving.value"
        :is-complete="form.isSectionComplete"
        @select="page.selectSection"
        @previous="page.previous"
        @next="page.next"
        @complete-review="page.saveAndExit"
        @show-corrections="page.openCorrections"
        @update="form.updateField"
        @update-nested="form.updateNested"
      />
      <SalesPropertyRegistrationFooter
        v-if="referenceLayout"
        :progress="propertyProgress"
        :saving="form.saving.value"
        @complete="page.saveAndExit"
        @save="page.saveAndExit"
      />
      <SalesPropertyReviewCorrectionsDialog
        :context="reviewContext"
        :show="page.correctionsOpen.value"
        @close="page.closeCorrections"
      />
    </template>
  </div>
</template>
